// ══════════════════════════════════════════════════════════════════════════════
//  Menus: MenuScrim (velo de menu), SplitBtn, MenuAcciones (kebab por portal) y HoverCard (detalle a solicitud). Los registros de modulo (velos y tarjeta viva) viven aqui con sus duenos.
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { C, T, R, E, M, Z } from '../../design.js';
import { Icon } from './Icon.js';

// ══════════════════════════════════════════════
//  MENU SCRIM — el clic afuera de un menú desplegado
//
//  2026-09-18 (dirección B del lienzo "Menús con profundidad", elegida por el
//  dueño: "ya no me agrada el efecto de degradar, mejor darle profundidad al
//  submenú"): el menú YA NO baja la app con un velo. Despega de ella con su
//  sombra (E.menu) y la app sigue a plena presencia. Esta capa sigue montada,
//  transparente, porque atrapa el clic afuera que cierra el menú. Pinta solo
//  con `conVelo`: la hoja inferior del teléfono (MenuHoja), que es casi un diálogo.
//  Lo de abajo explica el velo que tuvo y que conserva esa excepción.
//
//  Un menú abierto es una FUNCIÓN en curso, no una hoja encima de otra hoja.
//  Sin velo, un dropdown sobre una tabla densa compite con ella y el ojo tiene
//  que buscar dónde empieza; con el contexto un plano abajo, el menú es lo
//  único vivo y se lee como lo que es: la herramienta que se está usando.
//
//  Usa `C.scrimMenu`, no el de modal: aquí no se interrumpe la lectura de lo
//  que hay detrás, solo se manda al fondo. Sin blur a propósito — desenfocar
//  la página entera se paga en cada apertura sobre tablas largas y en tablet.
//
//  Se monta DENTRO de la capa del menú (no en un portal) para que el propio
//  disparador quede por encima del velo: el botón que abrió sigue encendido,
//  que es lo que ata el menú a su origen.
//
//  `portal` es la excepción obligada, no una preferencia: el header del shell
//  lleva `GLASS.chrome` con backdrop-filter, y un backdrop-filter distinto de
//  none convierte al elemento en BLOQUE CONTENEDOR de sus descendientes
//  `position: fixed`. Un velo montado dentro del header no cubriría el
//  viewport: sería una banda de 52px sobre el propio topbar. Los menús que
//  cuelgan del header lo montan con `portal` para escapar de ese contenedor.
// ══════════════════════════════════════════════
//  El velo NO corta la propagación del mousedown: componentes que no se
//  conocen entre sí cierran sus menús con un listener de `document`, y cortar
//  aquí los dejaría abiertos detrás del velo. Un clic en el velo cierra todo
//  lo que estuviera desplegado, que es lo que el usuario espera.
//  Dos menús de componentes distintos pueden quedar abiertos a la vez (basta
//  que el segundo disparador viva por encima del primer velo). Dos tintes de
//  0.22 se multiplican y el fondo se va al doble de lo que declara el token,
//  así que solo el primer velo vivo pinta; el resto captura el clic, invisible.
//
//  El registro es un Set de módulo con aviso, NO un contador leído en render.
//  Dos razones, las dos observadas: (1) cerrar un menú y abrir otro ocurre en
//  UN SOLO commit (`setCfgOpen(true); setKebabOpen(false)`), y en render el
//  velo entrante todavía vería vivo al saliente, quedándose transparente para
//  siempre; (2) si el velo que pinta se desmonta y otro sobrevive, alguien
//  tiene que volver a pintar. Por eso cada alta y cada baja RE-DERIVA quién es
//  el primero y avisa a todos. En `useLayoutEffect` porque los cleanups de las
//  bajas corren antes de los efectos de las altas del mismo commit, y el
//  repintado se descarga antes de que el navegador pinte: sin parpadeo.
const velosVivos = new Set();
const revisarVelos = () => {
  const primero = velosVivos.values().next().value;   // el Set conserva orden de alta
  velosVivos.forEach(avisar => avisar(avisar === primero));
};
const MenuScrim = ({ onClose, zIndex = 0, portal = false, conVelo = false }) => {
  const [pinta, setPinta] = useState(false);
  useLayoutEffect(() => {
    if (!conVelo) return undefined;
    velosVivos.add(setPinta);
    revisarVelos();
    return () => { velosVivos.delete(setPinta); revisarVelos(); };
  }, [conVelo]);
  const velo = (
    <div onMouseDown={() => onClose?.()} aria-hidden="true"
      style={{ position: "fixed", inset: 0, zIndex, background: pinta ? C.scrimMenu : "transparent", animation: `gbFade ${M.fast} ${M.ease}` }}/>
  );
  return portal ? createPortal(velo, document.body) : velo;
};

// ══════════════════════════════════════════════
//  SPLIT BTN — botón partido estilo Office (Pegar ▾ / Guardar como ▾)
//  El cuerpo dispara la acción principal (onClick) igual que un Btn; la
//  pestaña con chevron despliega el menú de variantes/opciones avanzadas.
//  `options` = [{ id, label, detalle, icon, onClick, disabled }]. La opción
//  por defecto también va en el menú (así se leen todas juntas, como en
//  Office); el disparador solo la ataja. Menú y velo siguen el canon de
//  MenuCinta: el conjunto sube a Z.menuLayer, el velo baja el contexto y el
//  disparador queda ENCIMA de él para que el menú cuelgue de su botón.
//  El velo va por PORTAL a Z.catcher (como los menús del breadcrumb): las
//  barras de documento usan GLASS.chrome y un backdrop-filter es bloque
//  contenedor de un fixed montado dentro (el velo se quedaría en la banda).
//  `onOpenChange(bool)`: si el botón vive dentro de una barra sticky con
//  z-index propio (contexto de apilamiento), esa barra debe subir a
//  Z.menuLayer mientras el menú está abierto o el velo la tapa junto con
//  el menú; el aviso existe para eso.
//  El MENÚ también va por portal, fijo bajo el botón y alineado a su borde
//  derecho (2026-10-07, el dueño: el menú de Nuevo oficio "queda por detrás del
//  workspace"): dentro del marco de una carpeta, que recorta lo que sobresale,
//  un menú absoluto quedaba cortado. Si no cabe abajo y arriba sí, abre hacia
//  arriba; si no cabe en ninguno, desplaza por dentro.
//  El primario va SIN borde, como Btn: un borde en C.primary sobre C.primaryFill
//  se veía como un aro claro de noche y lo hacía 2 px más alto que sus vecinos.
// ══════════════════════════════════════════════
const SplitBtn = ({ children, icon, onClick, options=[], variant="secondary", size="sm", disabled=false, loading=false, title, menuTitle, menuMinWidth=230, onOpenChange, style:sx={} }) => {
  const [open, setOpen] = useState(false);
  const anclaRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);   // { top, left, maxHeight } del menú fijo
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const colocar = () => {
      const r = anclaRef.current?.getBoundingClientRect();
      if (!r) return;
      const vw = window.innerWidth, vh = window.innerHeight, aire = 8;
      const w = menuRef.current?.offsetWidth || menuMinWidth;
      const h = menuRef.current?.scrollHeight || 0;
      const abajo = vh - r.bottom - 4 - aire, arriba = r.top - 4 - aire;
      const haciaArriba = h > abajo && arriba > abajo;
      const maxHeight = Math.max(120, haciaArriba ? arriba : abajo);
      setPos({
        left: Math.max(aire, Math.min(r.right - w, vw - w - aire)),
        top: haciaArriba ? Math.max(aire, r.top - 4 - Math.min(h, maxHeight)) : r.bottom + 4,
        maxHeight,
      });
    };
    colocar();
    window.addEventListener("resize", colocar);
    window.addEventListener("scroll", colocar, true);
    return () => { window.removeEventListener("resize", colocar); window.removeEventListener("scroll", colocar, true); };
  }, [open, menuMinWidth]);
  const onOpenRef = useRef(onOpenChange); onOpenRef.current = onOpenChange;
  useEffect(() => { onOpenRef.current?.(open); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const tecla = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", tecla);
    return () => document.removeEventListener("keydown", tecla);
  }, [open]);
  const isBusy = disabled || loading;
  const filled = variant === "primary";
  const typo   = size==="sm" ? { ...T.bodySm, fontWeight:600 } : { ...T.body, fontWeight:600 };
  const shell  = filled
    ? { background:C.primaryFill, color:C.onPrimary }
    : { background:C.bg,      color:C.textMed,   border:`1px solid ${C.border}` };
  const divisor   = filled ? C.onPrimaryBorder : C.border;
  const abiertoBg = filled ? C.primaryDark : C.surfaceSunken;
  const hoverCls  = filled ? " gb-fill-primary" : " gb-btn-soft";
  const seg = { background:"transparent", border:"none", color:"inherit", cursor:isBusy?"not-allowed":"pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center" };
  return (
    <div ref={anclaRef} style={{ position:"relative", display:"inline-flex", ...(open ? { zIndex:Z.menuLayer } : null), ...sx }}>
      {open && <MenuScrim onClose={() => setOpen(false)} zIndex={Z.catcher} portal/>}
      {/* Sin overflow:hidden (recortaría el anillo de foco): cada segmento
          redondea su propia esquina y el hover respeta la silueta. */}
      <div style={{ position:"relative", zIndex:1, display:"inline-flex", alignItems:"stretch", borderRadius:R.ctl, opacity:disabled?0.5:1, ...shell }}>
        <button type="button" onClick={isBusy?undefined:onClick} disabled={isBusy} title={title} aria-busy={loading||undefined}
          className={"gb-press gb-i gb-focus"+hoverCls}
          style={{ ...typo, ...seg, gap:8, padding: size==="sm"?"6px 12px":"8px 16px", borderRadius:`${R.ctl}px 0 0 ${R.ctl}px` }}>
          {loading
            ? <span className="gb-spin" style={{width:size==="sm"?13:14, height:size==="sm"?13:14, borderWidth:2, flexShrink:0}} aria-hidden/>
            : (icon && <Icon name={icon} size={size==="sm"?12:14} color="currentColor"/>)}
          {children}
        </button>
        <button type="button" onClick={() => setOpen(o => !o)} disabled={disabled}
          aria-haspopup="menu" aria-expanded={open} aria-label="Más opciones" title="Más opciones"
          className={"gb-press gb-i gb-focus"+hoverCls}
          style={{ ...seg, cursor:disabled?"not-allowed":"pointer", padding: size==="sm"?"0 7px":"0 9px", borderLeft:`1px solid ${divisor}`, borderRadius:`0 ${R.ctl}px ${R.ctl}px 0`, background: open ? abiertoBg : "transparent" }}>
          <Icon name="chevron-down" size={11} color="currentColor"/>
        </button>
      </div>
      {open && createPortal(
        <div ref={menuRef} role="menu" style={{ position:"fixed", top:pos ? pos.top : -9999, left:pos ? pos.left : -9999, visibility:pos ? "visible" : "hidden", minWidth:menuMinWidth, maxWidth:Math.max(menuMinWidth, 300), maxHeight:pos ? pos.maxHeight : "min(70vh, 520px)", overflowY:"auto", zIndex:Z.menuLayer + 1, background:C.flotante, border:`1px solid ${C.border}`, borderRadius:R.menu, boxShadow:E.menu, padding:4, animation:`gbPopDown ${M.fast} ${M.easeOut}` }}>
          {menuTitle && (
            <div style={{ fontSize:8.5, fontWeight:700, color:C.textMuted, textTransform:"uppercase", letterSpacing:0.5, padding:"6px 9px 3px" }}>{menuTitle}</div>
          )}
          {options.map(op => op.separator ? (
            <div key={op.id} role="separator" style={{ height:1, background:C.borderLight, margin:"4px 6px" }}/>
          ) : (
            <button key={op.id} type="button" role="menuitem" className="gb-ghost gb-i gb-press gb-focus" disabled={!!op.disabled}
              onClick={() => { setOpen(false); op.onClick?.(); }}
              style={{ display:"flex", alignItems:op.detalle?"flex-start":"center", gap:8, width:"100%", textAlign:"left", padding:"6px 9px", border:"none", borderRadius:R.ctl, background:"transparent", cursor:op.disabled?"not-allowed":"pointer", fontFamily:"inherit", opacity:op.disabled?0.45:1 }}>
              {op.icon && <Icon name={op.icon} size={13} color={C.textSub} style={{ flexShrink:0, marginTop:op.detalle?2:0 }}/>}
              <span style={{ display:"flex", flexDirection:"column", gap:1, minWidth:0 }}>
                <span style={{ fontSize:12, fontWeight:600, color:C.text, lineHeight:1.2 }}>{op.label}</span>
                {op.detalle && <span style={{ fontSize:10, color:C.textMuted, lineHeight:1.2 }}>{op.detalle}</span>}
              </span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// ══════════════════════════════════════════════
//  MENU ACCIONES — kebab anclado (fila de una lista, cabecera de un detalle)
//  Va por PORTAL: las celdas recortan y el contenedor de la tabla scrollea. El
//  borde derecho del menú coincide con el del disparador y se abre hacia
//  arriba cuando no queda espacio debajo (últimas filas). Menú = función: el
//  velo (MenuScrim en Z.catcher) baja el contexto y el disparador sube a
//  Z.menuLayer para leerse como origen del menú.
//  items: { id, icon, label, detalle, onClick, disabled, danger } o 'sep'.
// ══════════════════════════════════════════════
const MenuAcciones = ({ items, title = 'Más acciones', icon = 'more-horizontal', size = 26, className = '', style: sx = {} }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const tecla = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [open]);
  const abrir = (e) => {
    e.stopPropagation();
    const r = ref.current?.getBoundingClientRect();
    if (r) {
      const vw = window.innerWidth, vh = window.innerHeight;
      const abajo = vh - r.bottom > 220;
      setPos({ top: abajo ? r.bottom + 4 : undefined, bottom: abajo ? undefined : vh - r.top + 4, right: Math.max(8, vw - r.right) });
    }
    setOpen(o => !o);
  };
  return (
    <>
      <button ref={ref} type="button" onClick={abrir} aria-haspopup="menu" aria-expanded={open} title={title} aria-label={title}
        className={`gb-ghost gb-press gb-i gb-focus ${className}`}
        style={{ width: size, height: size, borderRadius: R.md, border: '1px solid transparent', background: 'transparent', color: C.textMuted,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer', flexShrink: 0,
          position: 'relative', zIndex: open ? Z.menuLayer : 'auto', ...sx,
          ...(open ? { border: `1px solid ${C.primaryMid}`, background: C.primaryLight, color: C.primary } : {}) }}>
        <Icon name={icon} size={Math.round(size * 0.5)} color="currentColor"/>
      </button>
      {open && pos && createPortal(
        <>
          <MenuScrim onClose={() => setOpen(false)} zIndex={Z.catcher}/>
          <div role="menu" onClick={e => e.stopPropagation()} style={{
            position: 'fixed', top: pos.top, bottom: pos.bottom, right: pos.right, zIndex: Z.menuLayer,
            minWidth: 240, maxWidth: 'calc(100vw - 16px)',
            background: C.flotante, border: `1px solid ${C.border}`, borderRadius: R.menu,
            boxShadow: E.menu, padding: 4, animation: `gbPopDown ${M.fast} ${M.easeOut}`,
          }}>
            <ItemsMenu items={items} onElegir={() => setOpen(false)} />
          </div>
        </>,
        document.body
      )}
    </>
  );
};

// Los renglones de un menú de acciones (kebab, clic secundario): un solo
// dibujo para que el mismo item se lea igual venga de donde venga.
const ItemsMenu = ({ items, onElegir }) => items.map((it, i) => it === 'sep'
  ? <div key={`sep${i}`} style={{ height: 1, background: C.borderLight, margin: '4px 6px' }}/>
  : (
    <button key={it.id} type="button" role="menuitem" disabled={it.disabled} className="gb-ghost gb-i gb-press gb-focus"
      onClick={(e) => { e.stopPropagation(); if (it.disabled) return; onElegir?.(); it.onClick(); }}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', textAlign: 'left', padding: '6px 9px', border: 'none', borderRadius: R.ctl,
        background: 'transparent', cursor: it.disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: it.disabled ? 0.55 : 1 }}>
      {it.icon && <Icon name={it.icon} size={13} color={it.danger && !it.disabled ? C.critical : C.textSub} style={{ flexShrink: 0, marginTop: 2 }}/>}
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: it.danger && !it.disabled ? C.critical : C.text, lineHeight: 1.2 }}>{it.label}</span>
        {it.detalle && <span style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.2 }}>{it.detalle}</span>}
      </span>
    </button>
  ));

// ══════════════════════════════════════════════
//  MENU CONTEXTUAL — el clic secundario sobre una fila (carpetas, 2026-09-13):
//  los MISMOS items que el kebab de esa fila, en el punto donde se hizo clic.
//  Mismo contrato de items y misma doctrina (velo MenuScrim en Z.catcher, menú
//  en Z.menuLayer, cierra con Escape, con el velo y al elegir). Se acota a la
//  ventana: si no cabe abajo o a la derecha, se abre hacia el otro lado.
//    <MenuContextual x={e.clientX} y={e.clientY} items={…} onClose={…} />
// ══════════════════════════════════════════════
const MenuContextual = ({ x, y, items, onClose }) => {
  useEffect(() => {
    const tecla = e => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [onClose]);
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
  const ancho = 240, alto = Math.min(vh - 16, 44 * items.length + 8);
  const left = x + ancho + 8 > vw ? Math.max(8, x - ancho) : x;
  const top  = y + alto + 8 > vh ? Math.max(8, y - alto) : y;
  return createPortal(
    <>
      <MenuScrim onClose={onClose} zIndex={Z.catcher}/>
      <div role="menu" onClick={e => e.stopPropagation()} onContextMenu={e => e.preventDefault()} style={{
        position: 'fixed', left, top, zIndex: Z.menuLayer, minWidth: ancho, maxWidth: 'calc(100vw - 16px)',
        background: C.flotante, border: `1px solid ${C.border}`, borderRadius: R.menu,
        boxShadow: E.menu, padding: 4, animation: `gbPopDown ${M.fast} ${M.easeOut}`,
      }}>
        <ItemsMenu items={items} onElegir={onClose} />
      </div>
    </>,
    document.body
  );
};

// ══════════════════════════════════════════════
//  MENU HOJA — el menú "Más" del teléfono: una hoja inferior con las acciones
//  agrupadas por a quién sirven (Documento, Cliente, Presupuesto…). Es la
//  forma móvil de MenuAcciones: mismo contrato de items, distinta geometría,
//  porque un dropdown de 240 px colgado de un botón de 40 no se lee ni se
//  toca en un teléfono. Menú = función: MenuScrim (sin blur) baja el contexto
//  y la hoja sube a Z.menuLayer, por encima del bottom nav y por debajo de
//  modales y toasts. Filas de 46 px (target táctil), pastilla de icono en
//  surfaceSunken, detalle en un renglón con elipsis; lo deshabilitado explica
//  su motivo en `detalle`. Cierra con el velo, con Escape y al elegir.
//  grupos: [{ id, titulo, items: [{ id, icon, label, detalle, onClick, disabled, danger }] }]
// ══════════════════════════════════════════════
const MenuHoja = ({ titulo, subtitulo, grupos = [], onClose }) => {
  useEffect(() => {
    const tecla = e => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [onClose]);
  const visibles = grupos.filter(g => g && g.items && g.items.length);
  // La hoja inferior nativa (2026-10-12, "GEOBAJA en el iPhone", dirección A): asa de 36 x 5, radio
  // R.hoja arriba, el fondo agrupado del teléfono y cada grupo como una tarjeta blanca con filas de 44
  // separadas por la línea fina (alineada con el texto, como en iOS); el icono en su tesela teñida de
  // acero. Es nativa (data-gb-nativo): la escala de letra del teléfono vive dentro.
  return createPortal(
    <>
      <MenuScrim onClose={onClose} zIndex={Z.menuLayer} conVelo/>
      <div role="menu" aria-label={titulo} data-gb-nativo="" onClick={e => e.stopPropagation()} style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: Z.menuLayer + 1,
        maxWidth: 560, margin: '0 auto', maxHeight: '88vh', overflowY: 'auto',
        background: C.bg, borderRadius: `${R.hoja}px ${R.hoja}px 0 0`, boxShadow: E.menu,
        padding: '6px 0 0', paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
        animation: `gbSheetUp ${M.slow} ${M.easeOut}`,
      }}>
        <div aria-hidden="true" style={{ width: 36, height: 5, borderRadius: R.pill, background: C.borderMid, margin: '2px auto 8px' }}/>
        {(titulo || subtitulo) && (
          <div style={{ padding: '4px 32px 2px' }}>
            {titulo && <div style={{ ...T.strong, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{titulo}</div>}
            {subtitulo && <div className="num-tabular" style={{ ...T.micro, color: C.textMuted, marginTop: 1 }}>{subtitulo}</div>}
          </div>
        )}
        {visibles.map(g => (
          <div key={g.id} style={{ marginTop: 6 }}>
            {g.titulo && (
              <div style={{ ...T.caption, color: C.textSub, padding: '12px 32px 6px' }}>{g.titulo}</div>
            )}
            <div style={{ margin: g.titulo ? '0 16px' : '8px 16px 0', background: C.flotante, borderRadius: R.card, overflow: 'hidden' }}>
              {g.items.map((it, i) => (
                <button key={it.id} type="button" role="menuitem" disabled={!!it.disabled} className="gb-ghost gb-i gb-press gb-focus"
                  onClick={() => { if (it.disabled) return; onClose?.(); it.onClick?.(); }}
                  style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 44, padding: '7px 16px', border: 'none', borderRadius: 0,
                    background: 'transparent', cursor: it.disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', textAlign: 'left', opacity: it.disabled ? 0.55 : 1 }}>
                  {i > 0 && <span aria-hidden="true" style={{ position: 'absolute', left: 58, right: 0, top: 0, height: 'var(--gb-hairline)', background: C.border }}/>}
                  <span style={{ width: 30, height: 30, borderRadius: R.md, background: it.danger && !it.disabled ? C.criticalLight : C.primaryLight, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {it.icon && <Icon name={it.icon} size={17} color={it.danger && !it.disabled ? C.critical : C.primary}/>}
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 }}>
                    <span style={{ ...T.body, fontWeight: 500, color: it.danger && !it.disabled ? C.critical : C.text }}>{it.label}</span>
                    {it.detalle && <span className="num-tabular" style={{ ...T.micro, color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.detalle}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>,
    document.body
  );
};

// ══════════════════════════════════════════════
//  HOVER CARD — detalle a solicitud: tarjeta flotante de LECTURA al pasar el
//  cursor (o al enfocar con teclado). Nunca en táctil: sin hover no hay
//  detalle a solicitud (matchMedia hover:none / pointer:coarse => inerte).
//  Envuelve al disparador en un span inline-flex que no altera el layout y
//  pinta la tarjeta por PORTAL en document.body (position fixed anclada al
//  disparador, acotada a la ventana), así funciona dentro de celdas y
//  contenedores con overflow. pointerEvents none: es contenido, no control.
//  Una sola tarjeta viva a la vez (registro de módulo). Sin `title` nativo.
//    <HoverCard content={<Detalle/>} placement="top|bottom|auto" maxWidth={320}>
//      <span>disparador</span>
//    </HoverCard>
// ══════════════════════════════════════════════
const HOVER_CARD_GAP = 6, HOVER_CARD_MARGEN = 8;
let hoverCardViva = null;   // cierre de la tarjeta visible, si hay una
const sinHover = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(hover: none)').matches || window.matchMedia('(pointer: coarse)').matches;
};
const HoverCard = ({ content, delay = 250, placement = 'top', maxWidth = 320, disabled = false, children }) => {
  const [abierta, setAbierta] = useState(false);
  const [pos, setPos] = useState(null);       // { top, left } medida; null = aún sin medir
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const cardRef = useRef(null);
  const timer = useRef(null);
  const inerte = disabled || content == null || content === '' || sinHover();

  const cerrar = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    setAbierta(false); setVisible(false); setPos(null);
    if (hoverCardViva === cerrar) hoverCardViva = null;
  }, []);
  const programar = () => {
    if (inerte || timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      if (hoverCardViva && hoverCardViva !== cerrar) hoverCardViva();
      hoverCardViva = cerrar;
      setAbierta(true);
    }, delay);
  };
  const cancelar = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (abierta) cerrar();
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); if (hoverCardViva === cerrar) hoverCardViva = null; }, [cerrar]);

  // Mide la tarjeta ya montada (invisible) y la ancla al disparador; el
  // siguiente frame la enciende para que corra la transición de entrada.
  useLayoutEffect(() => {
    if (!abierta) return undefined;
    const r = ref.current?.getBoundingClientRect();
    const c = cardRef.current?.getBoundingClientRect();
    if (!r || !c) return undefined;
    const vw = window.innerWidth, vh = window.innerHeight;
    const cabeArriba = r.top - HOVER_CARD_GAP - c.height >= HOVER_CARD_MARGEN;
    const cabeAbajo  = r.bottom + HOVER_CARD_GAP + c.height <= vh - HOVER_CARD_MARGEN;
    const arriba = placement === 'top' ? (cabeArriba || !cabeAbajo)
                 : placement === 'bottom' ? (!cabeAbajo && cabeArriba)
                 : (cabeArriba || !cabeAbajo);
    let top = arriba ? r.top - HOVER_CARD_GAP - c.height : r.bottom + HOVER_CARD_GAP;
    let left = r.left + r.width / 2 - c.width / 2;
    left = Math.max(HOVER_CARD_MARGEN, Math.min(left, vw - c.width - HOVER_CARD_MARGEN));
    top  = Math.max(HOVER_CARD_MARGEN, Math.min(top,  vh - c.height - HOVER_CARD_MARGEN));
    setPos({ top, left });
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [abierta, placement]);

  // Al hacer scroll o cambiar el tamaño la ancla se mueve: la tarjeta se retira.
  useEffect(() => {
    if (!abierta) return undefined;
    const fuera = () => cerrar();
    window.addEventListener('scroll', fuera, true);
    window.addEventListener('resize', fuera);
    return () => { window.removeEventListener('scroll', fuera, true); window.removeEventListener('resize', fuera); };
  }, [abierta, cerrar]);

  const handlers = inerte ? {} : { onMouseEnter: programar, onMouseLeave: cancelar, onFocus: programar, onBlur: cancelar };
  return (
    <>
      <span ref={ref} {...handlers} style={{ display: 'inline-flex', minWidth: 0, maxWidth: '100%' }}>{children}</span>
      {abierta && typeof document !== 'undefined' && createPortal(
        <div ref={cardRef} role="tooltip" style={{
          position: 'fixed', top: pos ? pos.top : 0, left: pos ? pos.left : 0, maxWidth,
          pointerEvents: 'none', zIndex: Z.popover,
          background: C.flotante, border: `1px solid ${C.border}`, borderRadius: R.card, boxShadow: E.e2,
          padding: '10px 12px', fontSize: 12, color: C.text, lineHeight: 1.45,
          opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(4px)',
          transition: `opacity ${M.fast} ${M.easeOut}, transform ${M.fast} ${M.easeOut}`,
          visibility: pos ? 'visible' : 'hidden',
        }}>
          {content}
        </div>,
        document.body
      )}
    </>
  );
};


export { HoverCard, MenuAcciones, MenuContextual, MenuHoja, MenuScrim, SplitBtn };
